import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/digital-development")({
  head: () => {
    const h = pageHead("digitalDevelopment", "en");
    return { meta: h.meta, links: h.links };
  },
  component: DigitalPage,
});

const steps = [
  { num: "01", title: "Problem & concept", body: "We clarify requirements, users, and processes — and define what the application must do, and what it deliberately shouldn't." },
  { num: "02", title: "Architecture & data model", body: "We lay the foundation: architecture, data models, and interfaces that hold up long-term and stay extensible." },
  { num: "03", title: "Development & integration", body: "Implementation on a React foundation — as a PWA, wrappable into a native app, with connections to existing systems and data sources." },
  { num: "04", title: "Operation & further development", body: "After go-live we support operation, scaling, and continuous further development — the application grows with the business." },
];

const kinds = [
  { title: "Progressive Web Apps (PWA)", body: "Applications that work like native apps in the browser — installable, offline-capable, fast. One codebase for all platforms." },
  { title: "Native apps via wrapping", body: "When app store presence or native functions are required, we wrap the PWA into a native app for iOS and Android — without having to maintain two separate apps." },
  { title: "SaaS & custom software", body: "Tailored applications with their own logic: internal tools, platforms, portals, or marketable SaaS products — on a modern React foundation." },
];

const capabilities = [
  { name: "Architecture & data models", detail: "long-term, extensible" },
  { name: "PWA development", detail: "installable, offline-capable, fast" },
  { name: "Native wrapping", detail: "iOS and Android from one codebase" },
  { name: "SaaS development", detail: "marketable products with their own logic" },
  { name: "Interfaces & APIs", detail: "connecting existing systems" },
  { name: "Frontend on React", detail: "modern, performant, independent" },
  { name: "Headless content with Sanity", detail: "where content needs to feed in structured" },
  { name: "Operation & further development", detail: "long-term, in partnership" },
];

const stack = [
  { name: "React", small: "frontend foundation" },
  { name: "PWA technologies", small: "cross-platform" },
  { name: "Sanity", small: "content layer where needed" },
];

function DigitalPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Services · Build"
          headline={<>When standard solutions fall short: <em>custom applications</em> that hold up.</>}
          lead="Some tasks can't be solved with a CMS or shop system. We build custom digital services — apps based on Progressive Web Apps that can be wrapped into native apps, as well as tailored SaaS and custom software on a React foundation."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Digital Development" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Custom software isn't <em>a throwaway project.</em> It's infrastructure.</>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Need a classic website or an online shop? Those are separate disciplines —{" "}
              <a href="/en/services/website-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">Website Development →</a>
              {" · "}
              <a href="/en/services/ecommerce-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">E-Commerce Development →</a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">When a process, a product, or a business model goes digital, it produces software that has to run every day. It isn't a campaign and it isn't a business card — it's infrastructure. That's exactly why the decisive factor here isn't a quick result, but a foundation that holds: clean architecture, well-considered data models, and code that's still extensible two years from now.</p>
              <p className="mb-5">In practice we keep seeing the same patterns: pieced-together tools that hit their limits, isolated solutions with no shared data basis, prototypes that become legacy burdens in production, and applications whose further development gets more expensive every month. The result: the software slows down the very business it was meant to accelerate.</p>
              <p>We build digital services as long-term infrastructure. It starts with the problem, not the technology: which process should be mapped, which users work with it, which systems need to be connected, and where the application has to be able to grow in the future.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="03" kicker="What we build" headline={<>Three kinds of <em>digital services.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {kinds.map((k) => (
                <div key={k.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{k.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{k.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="04" kicker="Approach" headline={<>Four phases from <em>the problem</em> to a viable application.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="05" kicker="PWA-First" headline={<>One codebase. <em>Runs everywhere.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-8">Instead of building two or three separate apps from the start, we take a PWA-first approach: the application is built as a Progressive Web App and runs immediately in the browser — installable, offline-capable, fast. When native presence in the app store or access to native device functions is needed, we wrap the same application into a native app.</p>
            </div>
            <div className="mt-2 rounded-lg border-2 border-lime bg-white p-7">
              <h3 className="mb-4 font-display text-xl text-ink">What that means in practice</h3>
              <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                <li>one codebase instead of three</li>
                <li>runs immediately in the browser, with no app store approval</li>
                <li>native apps for iOS and Android when needed</li>
                <li>significantly lower maintenance and development costs</li>
                <li>full creative and functional freedom</li>
              </ul>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Capabilities & stack" headline={<>How we make digital services <em>built to last.</em></>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="07" />

        <RelatedServices
          locale="en"
          fromId="entwicklung-digitaler-dienste"
          number="08"
          items={relatedBySource["entwicklung-digitaler-dienste"]}
        />

        <Section number="09" kicker="Engagement">
          <CTABlock
            headline={<>An application that <em>holds up</em> — instead of becoming legacy</>}
            body="Whether PWA, native app, or tailored SaaS — we first clarify what the problem actually requires. If you need an application that's cleanly built, grows with you, and holds up long-term, we'd be glad to talk."
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
        <GlossaryTerms locale="en" ids={["server-side-rendering", "single-page-application", "tanstack-start", "hydration", "core-web-vitals"]} />
      </main>
      <Footer />
    </>
  );
}
